// Locale-bound formatting lives with the catalogues: the active language is decided here.

import { UNKNOWN_VALUE } from './domain.ts'
import { locale, type Locale } from './locale.ts'

// Italian whatever the interface speaks: a bare 'en' is en-US, month first. Cost:
// docs/sviluppo/trappole-frontend.md § «Un locale a due lettere passato a Intl sceglie la nazione»
const DATE_LOCALE = 'it-IT'

// One resolved formatter instead of one per call: `toLocaleDateString` re-resolves the
// locale and the option bag every time, and a table formats two dates per row.
const dayFormat = new Intl.DateTimeFormat(DATE_LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

// The instant formatter, for the columns where the hour is the whole point: a box last
// seen two minutes ago and one last seen at 00:05 are the same day and not the same news.
const instantFormat = new Intl.DateTimeFormat(DATE_LOCALE, {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
  hour: '2-digit',
  minute: '2-digit',
})

// The day spelled out, for a date read as part of a sentence rather than in a column.
const longDayFormat = new Intl.DateTimeFormat(DATE_LOCALE, {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

/** A value the API sends as a `date`, with no time in it at all: `2026-01-31`. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/

// The branch is the trap, not a convenience: `new Date('2026-01-01')` is UTC midnight, so
// a date-only value renders as the day before anywhere behind UTC — invisible from Italy.
export function formatDate(iso: string): string {
  if (DATE_ONLY.test(iso)) {
    const [year, month, day] = iso.split('-')
    return dayFormat.format(new Date(Number(year), Number(month) - 1, Number(day)))
  }
  return dayFormat.format(new Date(iso))
}

/** A `date` spelled out — «1 gennaio 2005» — with the same local-midnight care as `formatDate`. */
export function formatLongDate(iso: string): string {
  const [year, month, day] = iso.split('-')
  return longDayFormat.format(new Date(Number(year), Number(month) - 1, Number(day)))
}

/** A `date-time` with its hour kept. Never for a `date`: there is no hour to show, and
 *  midnight UTC would be the day before — `formatDate` is the one that knows. */
export function formatDateTime(iso: string): string {
  return instantFormat.format(new Date(iso))
}

/** Today as a civil date, from the local parts. `toISOString().slice(0, 10)` is the UTC
 * day, which in Italy is yesterday before 02:00 in summer — the mirror of the trap above. */
export function todayIsoDate(): string {
  const now = new Date()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${String(now.getFullYear())}-${month}-${day}`
}

// Kickoff time alone, for the line that already names the day next to it.
const timeFormat = new Intl.DateTimeFormat(DATE_LOCALE, { hour: '2-digit', minute: '2-digit' })

/** The hour of a `date-time`. Never for a `date`: there is no hour in one. */
export function formatTime(iso: string): string {
  return timeFormat.format(new Date(iso))
}

// A kickoff as «sab 4 ott»: the weekday is what a coach plans the week by.
const matchDayFormat = new Intl.DateTimeFormat(DATE_LOCALE, {
  weekday: 'short',
  day: 'numeric',
  month: 'short',
})

/** The day of a `date-time` with its weekday, no year and no hour. */
export function formatDay(iso: string): string {
  return matchDayFormat.format(new Date(iso))
}

// «domenica 18 ottobre»: the day as a sentence says it, for a message sent to a group chat.
const longWeekdayDayFormat = new Intl.DateTimeFormat(DATE_LOCALE, {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
})

/** The day of a `date-time` spelled out with its weekday, no year and no hour. */
export function formatLongDay(iso: string): string {
  return longWeekdayDayFormat.format(new Date(iso))
}

// «ottobre 2026»: the heading a calendar puts over its month.
const monthFormat = new Intl.DateTimeFormat(DATE_LOCALE, { month: 'long', year: 'numeric' })

/** The month of a `date-time`, on the clocks of the pitch: 00:30 on the 1st is already the new one. */
export function formatMonth(iso: string): string {
  return monthFormat.format(new Date(iso))
}

// Explicit for the DATE_LOCALE reason, though region names agree across en/en-GB/en-US. See
// docs/sviluppo/trappole-frontend.md § «Un locale a due lettere passato a Intl sceglie la nazione»
const REGION_LOCALE: Record<Locale, string> = { en: 'en-GB', it: 'it-IT' }

// `auto` words only yesterday, today and tomorrow: past those it says «dopodomani» and
// «l'altro ieri», where the product wants the number («tra 2 giorni») — hence two formatters.
const listFormat = new Intl.ListFormat(REGION_LOCALE[locale], {
  style: 'long',
  type: 'conjunction',
})

/** «a, b e c»: the interface's own conjunction, not a comma-join that reads the same in every tongue. */
export function formatList(items: readonly string[]): string {
  return listFormat.format(items)
}

const alternativesFormat = new Intl.ListFormat(REGION_LOCALE[locale], {
  style: 'long',
  type: 'disjunction',
})

/** «A, B o C»: the interface's own «or», for a choice still open. */
export function formatAlternatives(items: readonly string[]): string {
  return alternativesFormat.format(items)
}

const relativeAuto = new Intl.RelativeTimeFormat(REGION_LOCALE[locale], { numeric: 'auto' })
const relativeAlways = new Intl.RelativeTimeFormat(REGION_LOCALE[locale], { numeric: 'always' })

const MS_PER_DAY = 86_400_000

/** The local civil day as a count since the epoch: subtracting two is whole days, DST or not. */
function civilDayNumber(date: Date): number {
  return Math.floor(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / MS_PER_DAY)
}

/** «oggi», «domani», «tra 2 giorni», «4 giorni fa»: local civil days, never milliseconds —
 *  a 00:30 kickoff seen at 23:30 the evening before is tomorrow. */
export function formatRelativeDay(iso: string, now: Date): string {
  const days = civilDayNumber(new Date(iso)) - civilDayNumber(now)
  const formatter = Math.abs(days) <= 1 ? relativeAuto : relativeAlways
  return formatter.format(days, 'day')
}

/** «oggi» and «ieri» for an instant just gone, and past them its day, «dom 4 ott»: what one says of a
 *  save, where «4 giorni fa» would be a sum to do. */
export function formatRecentDay(iso: string, now: Date): string {
  const days = civilDayNumber(new Date(iso)) - civilDayNumber(now)
  return Math.abs(days) <= 1 ? relativeAuto.format(days, 'day') : formatDay(iso)
}

/** Past two weeks a delay counts whole weeks: «34 giorni fa» is a sum the reader has to do. */
const DAYS_BEFORE_WEEKS = 14

function delayIn(
  iso: string,
  now: Date,
): { readonly amount: number; readonly unit: 'day' | 'week' } {
  const days = civilDayNumber(now) - civilDayNumber(new Date(iso))
  if (days < DAYS_BEFORE_WEEKS) return { amount: days, unit: 'day' }
  return { amount: Math.floor(days / 7), unit: 'week' }
}

/** How long ago a kickoff was: «4 giorni fa», «2 settimane fa». */
export function formatDelay(iso: string, now: Date): string {
  const { amount, unit } = delayIn(iso, now)
  const formatter = unit === 'day' && amount <= 1 ? relativeAuto : relativeAlways
  return formatter.format(-amount, unit)
}

/** The language names are written in, and so the language they sort in. */
export const SORT_LOCALE = REGION_LOCALE[locale]

const regionNames = new Intl.DisplayNames([REGION_LOCALE[locale]], { type: 'region' })

/** A nation's name in the interface language. `DisplayNames` answers with the code itself
 *  when it does not know the region, and a bare code on screen is what guardrail §5
 *  forbids: that case becomes UNKNOWN_VALUE. */
export function nationalityLabel(code: string): string {
  const named = regionNames.of(code)
  return named === undefined || named === code ? UNKNOWN_VALUE : named
}

const statFormat = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 })

/** A provider's count or share in the interface language: «55,6» in Italian, one decimal at most. */
export function formatStat(value: number): string {
  return statFormat.format(value)
}

/** An amount in the interface language: unlike a date, a price has no ambiguous spelling. */
export function formatPrice(amountCents: number, currency: string): string {
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountCents / 100)
}
